import CreditOverview from './CreditOverview';
import CreditInventoryDialog from './CreditInventoryDialog';
import { useCreditAccount } from '../../hooks/useCreditAccount';
import { subscribeCreditRefresh, notifyCreditRefresh } from '../../services/creditRefresh';
import SubscriptionPlans from './SubscriptionPlans';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { formatCredit, type CreditHeatmap, type CreditInventoryItem, type CreditResult } from '../../services/credit';

/** 账户页专用的区间统计与来源明细；余额由共享状态提供，未知统计绝不补零。 */
interface CreditSnapshot {
  week: CreditResult<CreditHeatmap> | null;
  month: CreditResult<CreditHeatmap> | null;
  inventory: CreditResult<CreditInventoryItem[]> | null;
}
const emptySnapshot: CreditSnapshot = { week: null, month: null, inventory: null };

/**
 * 账户积分总览与来源明细，复用平台事实和现有设置页样式。
 * 父页面按账号及登录时间设置 key，切账号立即卸载；服务层再次拦截迟到响应。
 */
export default function CreditUsageCard() {
  const [snapshot, setSnapshot] = useState<CreditSnapshot>(emptySnapshot);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const accountState = useCreditAccount();
  const loading = detailsLoading || accountState.loading;
  const [language, setLanguage] = useState(i18nService.getLanguage());
  const sequence = useRef(0);
  const mounted = useRef(false);
  const locale = language === 'en' ? 'en-US' : language === 'zh-TW' ? 'zh-TW' : 'zh-CN';
  const t = (key: string) => i18nService.t(`credit${key}`);

  /** 附加统计独立读取；余额统一由共享账户状态管理，避免侧栏和页面各存一份。 */
  const refreshDetails = useCallback(async () => {
    const current = ++sequence.current;
    setDetailsLoading(true);
    const [week, month, inventory] = await Promise.all([
      authService.credits.heatmap(7), authService.credits.heatmap(30), authService.credits.inventory(),
    ]);
    if (!mounted.current || current !== sequence.current) return;
    setDetailsLoading(false);
    setSnapshot([week, month, inventory].some(result => result.status === 'superseded')
      ? emptySnapshot : { week, month, inventory });
  }, []);

  /** 显式刷新同时读取总览和明细，不发送全局通知造成重复请求。 */
  const refresh = () => { void accountState.refresh(); void refreshDetails(); };

  useEffect(() => {
    mounted.current = true;
    void refreshDetails();
    const unsubscribeCredit = subscribeCreditRefresh(refreshDetails);
    /** 只比较登录身份；同账号资料/心跳更新不能关闭正在查看的明细。 */
    const readIdentity = () => authService.getSessionIdentity();
    let identity = readIdentity();
    // 真正退出/切换身份时，即使父页面尚未更新，也先丢弃旧数据和迟到响应。
    const unsubscribeAuth = authService.subscribe(() => {
      const next = readIdentity();
      if (identity === next) return;
      identity = next;
      sequence.current++; setSnapshot(emptySnapshot); setInventoryOpen(false); void refreshDetails();
    });
    /** 页面恢复焦点时刷新附加统计；不启动固定轮询或消耗模型额度。 */
    const onFocus = () => { if (document.visibilityState === 'visible') void refreshDetails(); };
    window.addEventListener('focus', onFocus);
    const unsubscribe = i18nService.subscribe(() => setLanguage(i18nService.getLanguage()));
    return () => { unsubscribeAuth(); unsubscribeCredit(); mounted.current = false; sequence.current += 1; window.removeEventListener('focus', onFocus); unsubscribe(); };
  }, [refreshDetails]);

  const account = accountState.account;
  const error = accountState.status === 'idle' || accountState.status === 'ready' ? null
    : accountState.status === 'unsupported' ? 'Unsupported' : accountState.status === 'unauthenticated' ? 'SignIn'
      : accountState.status === 'forbidden' ? 'Forbidden' : 'Unavailable';
  /** 金额仅在已取得平台单位时显示，未知金额保持占位。 */
  const amount = (value: number | null | undefined) => formatCredit(value, account?.credit_per_quota ?? 0, locale);

  return (
    <section aria-label={t('Title')} data-testid="credit-usage-card" className="space-y-3 text-claude-text dark:text-claude-darkText">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium">{t('Title')}</h4>
        <button type="button" onClick={() => void refresh()} disabled={loading} className="rounded-lg border border-claude-border dark:border-claude-darkBorder px-3 py-1.5 text-xs disabled:opacity-50">
          {t(loading ? 'Loading' : 'Refresh')}
        </button>
      </div>
      {error && <p role="status" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
        {t(error)} {account && t('Stale')}
      </p>}
      {!account && !error && <p role="status" className="text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('Loading')}</p>}
      {account && <>
        {account.mode !== 'live' && <p className="rounded-xl bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">{t(account.mode === 'shadow' ? 'Shadow' : 'Off')}</p>}
        {(account.credit_debt_mcr ?? 0) > 0 && <p role="status" className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-200">{t('Debt')}: {amount(account.credit_debt_mcr)} {t('Unit')}</p>}
        <CreditOverview account={account} week={snapshot.week} month={snapshot.month} locale={locale} onDetails={() => setInventoryOpen(true)} />
        {inventoryOpen && <CreditInventoryDialog result={snapshot.inventory} loading={detailsLoading} scale={account.credit_per_quota} locale={locale}
          onClose={() => setInventoryOpen(false)} onRefresh={refresh} />}
        {accountState.updatedAt && <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('Updated')}: {new Date(accountState.updatedAt).toLocaleString(locale)}</p>}
      </>}
      <SubscriptionPlans onPaid={notifyCreditRefresh} />
    </section>
  );
}
